import { useEffect, useMemo } from 'react';
import type { ConsoleLogger, StorageClient } from '../types.js';
import { AppId } from '../../../ApplicationWrapper.types';

type MigrationKey = ReturnType<Date['getTime']>;

const migrations: Record<MigrationKey, { description: string; migratorFunction: (localStorageClient: Storage) => void }> = {
  1744280764700: {
    description: 'Reset last used product environments to change value format',
    migratorFunction: (localStorageClient) => {
      localStorageClient.removeItem(`cld-${AppId.MediaAPI}-current-customer`);
      localStorageClient.removeItem(`cld-${AppId.DigitalAssetManagement}-current-customer`);
      localStorageClient.removeItem(`cld-${AppId.Settings}-current-customer`);
      localStorageClient.removeItem(`cld-${AppId.Video}-current-customer`);
      localStorageClient.removeItem(`cld-${AppId.MediaFlows}-current-customer`);
    },
  },
};

export const useLocalStorageMigrations = ({
  consoleLogger,
  unicornStorageClient,
  localStorageClient,
}: {
  unicornStorageClient: StorageClient;
  consoleLogger: ConsoleLogger;
  localStorageClient: Storage;
}) => {
  const lastAppliedMigration = useMemo(
    () => unicornStorageClient.createController<MigrationKey>('__legacy-last-applied-migration'),
    [unicornStorageClient]
  );

  useEffect(() => {
    const readResult = lastAppliedMigration.get();

    const orderedMigrations = Object.entries(migrations)
      .filter(([unixTimestamp, _]) => {
        if (readResult.status !== 'success') {
          return true;
        }

        const migrationTime = unixTimestamp;
        const lastAppliedTime = readResult.value;

        return Number(migrationTime) > lastAppliedTime;
      })
      .sort(([unixTimestampA, _], [unixTimestampB, __]) => {
        const dateA = unixTimestampA;
        const dateB = unixTimestampB;

        if (dateA > dateB) {
          return 1;
        }

        if (dateA < dateB) {
          return -1;
        }

        return 0;
      });

    if (orderedMigrations.length === 0 && readResult.status === 'success') {
      return consoleLogger.debug('general', 'No local storage migrations to apply', {
        lastAppliedMigration: { timestamp: readResult.value, description: migrations[readResult.value] },
      });
    }

    try {
      orderedMigrations.forEach(([unixTimestamp, { description, migratorFunction }]) => {
        migratorFunction(localStorageClient);
        lastAppliedMigration.set(Number(unixTimestamp));

        consoleLogger.debug('general', 'Applied local storage migration', {
          unixTimestamp,
          migrationDescription: description,
        });
      });
    } catch (error: unknown) {
      consoleLogger.error('general', 'Local storage migrations failed', error);
    }
  }, [localStorageClient, lastAppliedMigration, consoleLogger]);
};
